import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Skeleton,
  Chip,
} from '@mui/material';
import {
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  TrendingUp as TrendingIcon,
  AllInclusive as TotalIcon,
} from '@mui/icons-material';
import { AIInstructionStats } from '../../types/aiInstruction';
import { useMobileLayout } from '../../hooks/useMobileLayout';

interface KeywordStatsProps {
  stats: AIInstructionStats | null;
  loading: boolean;
}

const KeywordStats: React.FC<KeywordStatsProps> = ({ stats, loading }) => {
  const { isSmallMobile, spacing } = useMobileLayout();

  const statCards = [
    {
      title: 'Celkem klíčových slov',
      value: stats?.totalKeywords || 0,
      icon: <TotalIcon />,
      color: 'primary',
    },
    {
      title: 'Aktivních',
      value: stats?.activeKeywords || 0,
      icon: <ActiveIcon />,
      color: 'success',
    },
    {
      title: 'Neaktivních',
      value: stats?.inactiveKeywords || 0,
      icon: <InactiveIcon />,
      color: 'warning',
    },
    {
      title: 'Celkem použití',
      value: stats?.totalUsageCount || 0,
      icon: <TrendingIcon />,
      color: 'info',
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={spacing.xs}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{ p: spacing.sm }}>
              <Skeleton variant="rectangular" height={80} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={spacing.xs}>
      {statCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Paper
            sx={{
              p: spacing.sm,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  color: `${card.color}.main`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {card.icon}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {card.title}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold" color={`${card.color}.main`}>
              {card.value}
            </Typography>
          </Paper>
        </Grid>
      ))}

      {/* Most Used Keywords */}
      {stats && stats.mostUsed.length > 0 && (
        <Grid item xs={12}>
          <Paper sx={{ p: spacing.sm }}>
            <Typography variant="h6" gutterBottom>
              Nejpoužívanější klíčová slova
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {stats.mostUsed.map((keyword) => (
                <Chip
                  key={keyword.id}
                  label={`${keyword.keyword} (${keyword.usageCount}×)`}
                  color="primary"
                  variant="outlined"
                  size={isSmallMobile ? 'small' : 'medium'}
                />
              ))}
            </Box>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default KeywordStats;

